
import React from 'react';
import { Testimonial } from '../../data/testimonialData';
import RatingStars from './RatingStars';
import { motion } from 'framer-motion';

interface TestimonialCardProps {
  testimonial: Testimonial & { cityImage?: string };
  isPartner?: boolean;
  partnerUrl?: string;
  index?: number;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
  testimonial, 
  isPartner = false, 
  partnerUrl,
  index = 0
}) => {
  const { name, location, rating, text, service } = testimonial;

  // Animation variants for the card
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut" as const
      }
    }
  };
  
  const CardContent = () => (
    <motion.div 
      className="bg-white rounded-xl shadow-md border border-gray-100 h-full hover:shadow-lg transition-shadow duration-300"
      variants={cardVariants}
      whileHover={{ 
        y: -5,
        transition: { duration: 0.2 }
      }}
    >
      <div className="p-6 flex flex-col h-full">
        {/* Rating Stars */}
        <motion.div 
          className="mb-3"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <RatingStars rating={rating} />
        </motion.div>
        
        {/* Testimonial Text */}
        <motion.p 
          className="text-navy/80 text-sm leading-relaxed mb-4 flex-grow italic"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          "{text}"
        </motion.p>
        
        {/* Author Info */}
        <motion.div 
          className="pt-4 border-t border-gray-100"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-bold text-navy text-sm">{name}</p>
              <p className="text-xs text-navy/60">{location}</p>
            </div>
            <span className="text-xs font-semibold text-orange bg-orange/10 px-2 py-1 rounded-full whitespace-nowrap">
              {service}
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // If it's a partner testimonial with a URL, wrap in a link
  if (isPartner && partnerUrl) {
    return (
      <a 
        href={partnerUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block h-full"
      >
        <CardContent />
      </a>
    );
  }

  return <CardContent />;
};

export default TestimonialCard;
